import {
  invoiceDownloadSchema,
  invoiceSchema,
  orderInvoicesSchema,
  salesRegisterSchema,
  type Invoice,
  type InvoiceDownload,
  type InvoicePaperSize,
  type IssueInvoiceInput,
  type OrderInvoices,
  type SalesRegister,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';

/**
 * Every invoicing call the app makes.
 *
 * Note what is absent, and permanently: an "edit invoice" and a "delete invoice". An issued
 * document is written once — the API refuses both — so offering either here would be offering
 * a button that cannot work. A correction is `creditInvoice`, which is a different word for a
 * different act.
 */

const INVOICES = '/invoices';

export async function getOrderInvoices(client: ApiClient, orderId: string): Promise<OrderInvoices> {
  return await client.get(`${INVOICES}/order/${orderId}`, { schema: orderInvoicesSchema });
}

export async function issueInvoice(
  client: ApiClient,
  orderId: string,
  input: IssueInvoiceInput = {},
): Promise<Invoice> {
  return await client.post(INVOICES, { orderId, ...input }, { schema: invoiceSchema });
}

export async function creditInvoice(
  client: ApiClient,
  invoiceId: string,
  reason: string,
): Promise<Invoice> {
  return await client.post(
    `${INVOICES}/${invoiceId}/credit-note`,
    { reason },
    { schema: invoiceSchema },
  );
}

export async function sendInvoice(
  client: ApiClient,
  invoiceId: string,
  paperSize: InvoicePaperSize = 'a4',
): Promise<Invoice> {
  return await client.post(
    `${INVOICES}/${invoiceId}/send`,
    { paperSize },
    { schema: invoiceSchema },
  );
}

/**
 * A short-lived link to the stored PDF. `refresh` re-renders it first, which is how the paid
 * and balance figures on a printed bill stay current after a customer settles up.
 */
export async function getInvoiceDownload(
  client: ApiClient,
  invoiceId: string,
  paperSize: InvoicePaperSize,
  options: { refresh?: boolean } = {},
): Promise<InvoiceDownload> {
  return await client.get(`${INVOICES}/${invoiceId}/download`, {
    schema: invoiceDownloadSchema,
    query: { paperSize, ...(options.refresh ? { refresh: 'true' } : {}) },
  });
}

export interface SalesRegisterParams {
  from: string;
  to: string;
}

export async function getSalesRegister(
  client: ApiClient,
  params: SalesRegisterParams,
): Promise<SalesRegister> {
  return await client.get(`${INVOICES}/sales-register`, {
    schema: salesRegisterSchema,
    query: { from: params.from, to: params.to },
  });
}

/** The same report as a file. The browser is sent to the API, which answers with a CSV body. */
export function salesRegisterCsvPath(params: SalesRegisterParams): string {
  const query = new URLSearchParams({ from: params.from, to: params.to, format: 'csv' });
  return `${INVOICES}/sales-register?${query.toString()}`;
}
